import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import ProductCard, { BottleGlyph } from './ProductCard';
import { placeholderFor } from './productShopMapper';
import { sanitizeRichText } from '../product/richText';
import PortfolioGalleryModal from '../newSalon/SalonProfilePage/PortfolioGalleryModal';

// Product detail: a swipeable image carousel (snap-scroll + dots + N/total),
// name / price / brand / description, a "you'll love" related rail, and a
// sticky qty + Add to Basket bar.

const MinusIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" aria-hidden><path d="M5 12h14" /></svg>
);
const PlusIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" aria-hidden><path d="M12 5v14M5 12h14" /></svg>
);
const ChevronLeftIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden><path d="M15 18l-6-6 6-6" /></svg>
);
const ChevronRightIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden><path d="M9 18l6-6-6-6" /></svg>
);

const ProductDetail = ({ product, products, currency = '£', cartItems = [], onAdd, onSetQty, onViewBasket, onOpen }) => {
  // Retail variants: one dropdown per option (Colour, Size...). The price,
  // stock state and basket line all follow the selected combination.
  const hasVariants = Array.isArray(product.variants) && product.variants.length > 0;
  const [selection, setSelection] = React.useState({});
  React.useEffect(() => { setSelection({}); }, [product.id]);
  const selectionComplete = hasVariants && (product.options || []).every((o) => selection[o.name]);
  const selectedVariant = selectionComplete
    ? (product.variants || []).find((v) => (product.options || []).every((o) => v.optionValues[o.name] === selection[o.name]))
    : null;
  const displayPrice = selectedVariant ? selectedVariant.price : product.price;
  const displayInStock = hasVariants
    ? (selectedVariant ? selectedVariant.inStock !== false : true)
    : product.inStock !== false;
  const cartQty = (cartItems || []).find((i) =>
    i.id === product.id && String(i.variantId || '') === String(selectedVariant ? selectedVariant.id : '')
  )?.qty || 0;

  const [idx, setIdx] = useState(0);
  const [viewerOpen, setViewerOpen] = useState(false);
  const [viewerIndex, setViewerIndex] = useState(0);
  const heroRef = useRef(null);
  const carouselRef = useRef(null);
  // Transform-driven drag, matching the salon profile hero. This used to be a
  // native scroll-snap container with drag writing straight to scrollLeft,
  // which fought the browser's own snapping: releasing re-enabled
  // scroll-snap-type at the same moment a smooth scrollTo was animating, and
  // the index came from a 70ms debounced scroll listener. That combination is
  // what made it feel janky. Position is now state we own outright.
  const [dragStart, setDragStart] = useState(null);
  const [dragDelta, setDragDelta] = useState(0);
  // Tap vs. drag: a clean tap (no meaningful move) opens the full-screen viewer;
  // a drag/swipe moves the carousel instead.
  const tap = useRef({ x: 0, y: 0, moved: false });
  const MIN_SWIPE = 50;

  const images = product.images && product.images.length ? product.images : [null];
  const related = products.filter((p) => p.category === product.category && p.id !== product.id).slice(0, 6);

  // Variant photos are assigned per choice of the FIRST option (e.g. one per
  // colour) and always come from the product gallery — so picking that choice
  // just swipes the carousel to its photo.
  const firstOptName = hasVariants && product.options && product.options.length ? product.options[0].name : null;
  const pickedFirst = firstOptName ? selection[firstOptName] : null;
  useEffect(() => {
    if (!pickedFirst) return;
    const match = (product.variants || []).find((v) => v.optionValues[firstOptName] === pickedFirst && v.image);
    if (!match) return;
    const i = images.indexOf(match.image);
    if (i >= 0) setIdx(i);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pickedFirst]);

  // Arrows + dots. Just sets the index now; the transform follows from it.
  const goTo = (i) => setIdx(Math.max(0, Math.min(images.length - 1, i)));

  /* One drag model for mouse and touch. Both track live so the image follows
     the cursor/finger rather than only jumping on release — the old code only
     did that for the mouse and leaned on native scrolling for touch, which is
     gone now that position is a transform. */
  const beginDrag = (x, y) => {
    tap.current = { x, y, moved: false };
    if (images.length < 2) return;
    setDragStart(x);
    setDragDelta(0);
  };
  const moveDrag = (x, y) => {
    if (!tap.current.moved && Math.abs(x - tap.current.x) + Math.abs(y - tap.current.y) > 8) {
      tap.current.moved = true;
    }
    if (dragStart === null) return;
    setDragDelta(x - dragStart);
  };
  const endDrag = () => {
    if (dragStart !== null) {
      if (dragDelta < -MIN_SWIPE && idx < images.length - 1) setIdx((i) => i + 1);
      else if (dragDelta > MIN_SWIPE && idx > 0) setIdx((i) => i - 1);
      // Reset regardless, so a short drag springs back to the current image.
      setDragStart(null);
      setDragDelta(0);
    }
    // A clean tap (no drag/swipe) opens the full-screen viewer.
    if (!tap.current.moved && images[idx]) openViewer(idx);
  };

  const onMouseDown = (e) => {
    if (e.button !== 0) return;
    beginDrag(e.clientX, e.clientY);
  };
  const onMouseMove = (e) => {
    if (dragStart !== null) e.preventDefault(); // kill the native image-drag ghost
    moveDrag(e.clientX, e.clientY);
  };
  const onTouchStart = (e) => beginDrag(e.targetTouches[0].clientX, e.targetTouches[0].clientY);
  const onTouchMove = (e) => moveDrag(e.targetTouches[0].clientX, e.targetTouches[0].clientY);

  /* A drag released outside the carousel never fires the element's own mouseup,
     which would leave the image stuck mid-drag. */
  useEffect(() => {
    if (dragStart === null) return undefined;
    window.addEventListener('mouseup', endDrag);
    return () => window.removeEventListener('mouseup', endDrag);
  });

  // Open the full-screen image viewer (same component as the salon gallery).
  const openViewer = (i) => { setViewerIndex(i); setViewerOpen(true); };

  // Add the first one: the stepper slides in and the button morphs to "View
  // Basket", which is the confirmation (plus the header basket catch).
  const handleAdd = () => {
    if (hasVariants && !selectedVariant) return; // button disabled until chosen
    onAdd(product.id, 1, selectedVariant ? selectedVariant.id : null);
  };

  return (
    <div className="ps-detail">
      <div className="ps-carousel-wrap">
        <div
          className={`ps-carousel${images.length > 1 ? ' is-draggable' : ''}${dragStart !== null ? ' is-dragging' : ''}`}
          ref={carouselRef}
          onMouseDown={onMouseDown}
          onMouseMove={onMouseMove}
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
          onTouchEnd={endDrag}
        >
          <div
            className="ps-carousel-track"
            style={{ transform: `translateX(calc(-${idx * 100}% + ${dragDelta}px))` }}
          >
            {images.map((img, i) => (
              <div
                key={i}
                ref={i === 0 ? heroRef : null}
                className={`ps-carousel-slide${img ? ' is-zoomable' : ''}`}
                style={img ? { backgroundImage: `url(${img})` } : { background: placeholderFor(product.name.length + i) }}
              >
                {!img && <span className="ps-card-glyph" aria-hidden><BottleGlyph size={64} /></span>}
              </div>
            ))}
          </div>
        </div>
        {images.length > 1 && (
          <>
            <button
              type="button"
              className="ps-carousel-nav ps-carousel-prev"
              onClick={() => goTo(idx - 1)}
              disabled={idx === 0}
              aria-label="Previous image"
            >
              <ChevronLeftIcon />
            </button>
            <button
              type="button"
              className="ps-carousel-nav ps-carousel-next"
              onClick={() => goTo(idx + 1)}
              disabled={idx === images.length - 1}
              aria-label="Next image"
            >
              <ChevronRightIcon />
            </button>
            <span className="ps-carousel-count">{idx + 1} / {images.length}</span>
          </>
        )}
      </div>

      {images.length > 1 && (
        <div className="ps-dots">
          {images.map((_, i) => (
            <button
              key={i}
              type="button"
              className={`ps-dot ${i === idx ? 'is-active' : ''}`}
              onClick={() => goTo(i)}
              aria-label={`Go to image ${i + 1}`}
              aria-current={i === idx}
            />
          ))}
        </div>
      )}

      <div className="ps-detail-body">
        <h2 className="ps-detail-name">{product.name}</h2>
        <p className="ps-detail-price">{currency}{(displayPrice || 0).toFixed(2)}</p>
        {hasVariants && (
          <div className="ps-variant-pickers">
            {(product.options || []).map((o) => (
              <div key={o.name} className="ps-variant-group">
                <div className="ps-variant-label">
                  {o.name}{selection[o.name] ? <span className="ps-variant-chosen">{selection[o.name]}</span> : null}
                </div>
                <div className="ps-variant-pills">
                  {o.values.map((v) => {
                    // A value is a dead end if no variant matches it together
                    // with the choices already made on the OTHER options.
                    const possible = (product.variants || []).some((vt) =>
                      vt.optionValues[o.name] === v &&
                      (product.options || []).every((other) =>
                        other.name === o.name || !selection[other.name] || vt.optionValues[other.name] === selection[other.name]
                      )
                    );
                    const active = selection[o.name] === v;
                    return (
                      <button
                        type="button"
                        key={v}
                        className={`ps-varpill${active ? ' is-active' : ''}${!possible ? ' is-unavailable' : ''}`}
                        onClick={() => setSelection((prev) => ({ ...prev, [o.name]: v }))}
                      >
                        {v}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
            {selectionComplete && !selectedVariant && (
              <p style={{ fontSize: 12, color: '#b91c1c', margin: 0 }}>That combination isn't available.</p>
            )}
          </div>
        )}
        {product.brand && <p className="ps-detail-brand">{product.brand}</p>}
        {product.description && (
          <div className="ps-detail-desc" dangerouslySetInnerHTML={{ __html: sanitizeRichText(product.description) }} />
        )}

        {related.length > 0 && (
          <div className="ps-related">
            <h3 className="ps-related-title">Other products you'll love</h3>
            <div className="ps-related-rail">
              {related.map((p) => (
                <div className="ps-related-item" key={p.id}>
                  <ProductCard product={p} currency={currency} onOpen={onOpen} onAdd={onAdd} compact />
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="ps-detail-bar">
        <div className={`ps-qty${cartQty < 1 ? ' is-collapsed' : ''}`} aria-hidden={cartQty < 1}>
          <button type="button" className="ps-qty-btn" onClick={() => onSetQty(product.id, cartQty - 1, selectedVariant ? selectedVariant.id : null)} aria-label="Remove one from basket"><MinusIcon /></button>
          <span className="ps-qty-num" key={cartQty}>{cartQty}</span>
          <button type="button" className="ps-qty-btn" onClick={() => onSetQty(product.id, cartQty + 1, selectedVariant ? selectedVariant.id : null)} aria-label="Add one to basket"><PlusIcon /></button>
        </div>
        {!displayInStock && cartQty < 1 ? (
          <button type="button" className="ps-btn ps-btn--primary ps-addbtn" disabled>
            <span className="ps-addbtn-label">Out of stock</span>
          </button>
        ) : (
          <button
            type="button"
            className="ps-btn ps-btn--primary ps-addbtn"
            disabled={hasVariants && !selectedVariant && cartQty < 1}
            onClick={cartQty > 0 ? onViewBasket : handleAdd}
          >
            <span className="ps-addbtn-label" key={cartQty > 0 ? 'view' : 'add'}>
              {cartQty > 0 ? 'View Basket' : hasVariants && !selectedVariant ? 'Choose options' : 'Add to Basket'}
            </span>
          </button>
        )}
      </div>

      {viewerOpen && createPortal(
        <PortfolioGalleryModal
          isOpen
          onClose={() => setViewerOpen(false)}
          images={images.filter(Boolean)}
          initialIndex={viewerIndex}
        />,
        document.body
      )}
    </div>
  );
};

export default ProductDetail;
